import { useCallback } from "react";
import { useConfirmDialog, type ConfirmDialogConfig } from "@tabipla/hooks/confirm-dialog";
import { AlertTriangle, Trash2 } from "lucide-react";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@tabipla/ui";

/**
 * Options for the confirm function
 * Extends ConfirmDialogConfig with variant support
 */
export interface ConfirmOptions extends Omit<ConfirmDialogConfig, "destructive"> {
  /** Visual variant - 'destructive' shows trash icon and red button */
  variant?: "default" | "destructive";
}

/**
 * Return type for useConfirm hook
 */
export interface UseConfirmReturn {
  /** Open the confirm dialog and return a promise that resolves to true if confirmed */
  confirm: (options: ConfirmOptions) => Promise<boolean>;
  /** The ConfirmDialog component to render (includes icons) */
  ConfirmDialog: () => JSX.Element | null;
}

/**
 * Hook for showing confirmation dialogs with icons.
 * Provides a promise-based API similar to window.confirm() but with a modern UI.
 *
 * @example
 * ```tsx
 * import { useConfirm } from "@tabipla/components";
 *
 * function MyComponent() {
 *   const { confirm, ConfirmDialog } = useConfirm();
 *
 *   const handleDelete = async () => {
 *     const confirmed = await confirm({
 *       title: "削除しますか？",
 *       description: "この操作は取り消せません。",
 *       confirmText: "削除",
 *       cancelText: "キャンセル",
 *       variant: "destructive",
 *     });
 *
 *     if (confirmed) {
 *       // Perform delete
 *     }
 *   };
 *
 *   return (
 *     <>
 *       <button onClick={handleDelete}>削除</button>
 *       <ConfirmDialog />
 *     </>
 *   );
 * }
 * ```
 */
export function useConfirm(): UseConfirmReturn {
  const { dialogState, confirm: baseConfirm, closeDialog } = useConfirmDialog();

  const confirm = useCallback(
    (options: ConfirmOptions): Promise<boolean> => {
      const { variant, confirmText, cancelText, ...rest } = options;
      return baseConfirm({
        ...rest,
        confirmText: confirmText ?? "確認",
        cancelText: cancelText ?? "キャンセル",
        destructive: variant === "destructive",
      });
    },
    [baseConfirm]
  );

  const ConfirmDialogComponent = useCallback(() => {
    if (!dialogState.isOpen) return null;

    const isDestructive = dialogState.destructive;

    return (
      <AlertDialog open={dialogState.isOpen} onOpenChange={(open) => !open && closeDialog()}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <div className="mx-auto mb-4 flex h-12 w-12 items-center justify-center rounded-full bg-destructive/10 sm:mx-0">
              {isDestructive ? (
                <Trash2 className="h-6 w-6 text-destructive" />
              ) : (
                <AlertTriangle className="h-6 w-6 text-amber-500" />
              )}
            </div>
            <AlertDialogTitle>{dialogState.title}</AlertDialogTitle>
            <AlertDialogDescription>{dialogState.description}</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel onClick={closeDialog}>
              {dialogState.cancelText || "キャンセル"}
            </AlertDialogCancel>
            <AlertDialogAction
              onClick={dialogState.onConfirm}
              className={isDestructive ? "bg-destructive hover:bg-destructive/90" : ""}
            >
              {dialogState.confirmText || "確認"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    );
  }, [dialogState, closeDialog]);

  return {
    confirm,
    ConfirmDialog: ConfirmDialogComponent,
  };
}
